import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import isEqual from 'lodash.isequal'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { usePATransactionsFile } from '@pages/PaymentAgent/TransactionHistory/hooks/usePATransactionsFile'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { prefixFilter } from '@utils/filter.utils'
import { getScrollToCardId } from '@utils/getItemId'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'
import { TransactionDto } from '@model/TransactionDto'
import { WalletDto, WalletTypeEnum, isWalletPAType } from '@model/WalletDto'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
  useScrollToElementIds,
} from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'
import { TransactionHistoryDrawerModal } from '@shared/components/TransactionHistoryDrawer'
import {
  TransactionHistoryEmptyState,
  TransactionHistoryMobileRow,
} from '@shared/components/TransactionHistoryTable'
import { ActionModal } from '@shared/modals/Action.modal'

import { PaymentAgentTransactionHistoryTable } from './PaymentAgentTransactionHistoryTable'

interface SearchProps extends FilterQueryProps {
  ca_search_ClientAmountFrom?: string
  ca_search_ClientAmountTo?: string
  ca_search_DateFrom?: string
  ca_search_DateTo?: string
  ca_search_TransactionTypeId?: string
  ca_search_TransactionStateId?: string
  ca_search_WalletId?: string
}

export const PaymentAgentTransactionHistoryPage: React.FC<{ walletId?: string }> = () => {
  const location = useLocation()
  const [auth] = useContext(AuthSessionContext)
  const { scrollIntoView } = useScrollToElementIds()
  const { t } = useTranslation()

  const isMobile = useWindowResize()

  const [wallets, setWallets] = useState<WalletDto[]>([])
  const { walletId } = location.state || {}

  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const [searchTerm, showSearchTerm] = useState<{ show: boolean; searchTerm: string | null }>({
    show: false,
    searchTerm: null,
  })

  const initialSearchValue: SearchProps = {
    ca_search_ClientAmountFrom: '',
    ca_search_ClientAmountTo: '',
    ca_search_DateFrom: undefined,
    ca_search_DateTo: undefined,
    ca_search_TransactionStateId: undefined,
    ca_search_TransactionTypeId: undefined,
    ca_search_WalletId: undefined,
  }
  const [search, setSearch] = useState<SearchProps>(initialSearchValue)
  const activeWalletId = search.ca_search_WalletId ?? walletId
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  useEffect(() => {
    ;(async () => {
      const w = await apiClient.getWallets()
      setWallets(w.filter((wallet) => isWalletPAType(wallet.walletType.id)))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEqual(search, initialSearchValue)) {
      setSearch(initialSearchValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, wallets])

  const locale = useSessionLanguage()

  const searchKey = {
    ...search,
    walletsLength: wallets.length,
    walletId,
    accountId: account?.id ?? auth?.id,
  }

  const { data, meta, setPageQuery, pageQuery, isFetching, forceRefresh, hasInitialResults } =
    usePaginatedQuery({
      queryKey: queryKeys.paymentAgent.transactionHistory(searchKey, locale),
      queryFn: async (pageQuery) => {
        const {
          ca_search_ClientAmountFrom,
          ca_search_ClientAmountTo,
          ca_search_WalletId,
          ...clearedSearch
        } = search

        return await apiClient.getTransactions({
          ...pageQuery,
          caller: 'ca',
          search: { ...clearedSearch },
          ca_search_ClientAmountFrom: prefixFilter({
            prefix: account?.id ?? auth?.id,
            value: ca_search_ClientAmountFrom,
          }),
          ca_search_ClientAmountTo: prefixFilter({
            prefix: account?.id ?? auth?.id,
            value: ca_search_ClientAmountTo,
          }),
          ca_search_ClientWalletType: prefixFilter({
            prefix: account?.id ?? auth?.id,
            value: WalletTypeEnum.PAYMENT_AGENT.toString(),
          }),
          ca_search_Client: auth?.id || '',
          ca_search_WalletId:
            ca_search_WalletId ?? (walletId ? walletId : wallets.map((x) => x.id).toString() || ''),
          languageId: locale,
        })
      },
    })

  const generateDocumentFile = usePATransactionsFile(data, activeWalletId)

  const filterWalletIds = walletId ? walletId : wallets.map((x) => x.id).toString() || ''

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.paymentAgent.filterOptions(locale),
    queryFn: () => apiClient.getPATransactionHistoryFilterOptions(account!.id, filterWalletIds),
    enabled: !!account?.id && !!(walletId || wallets.length),
  })
  const transactionTypes = filterOptions?.transactionTypes as NameDto[] | undefined
  const transactionStates = filterOptions?.transactionStates as NameDto[] | undefined
  const walletNameOptions: NameDto<string>[] | undefined = wallets.map((w) => ({
    id: w.id,
    name: `${w.currency.id} · ${w.name}`,
  }))

  const onCancelTransaction = async (transaction: TransactionDto) => {
    await apiClient.cancelTransaction(transaction.id)
    queryClient.invalidateQueries({ queryKey: ['transactionHistory'] })
    queryClient.invalidateQueries({ queryKey: ['wallet'] })
    queryClient.invalidateQueries({
      queryKey: queryKeys.transaction.statusHistory(transaction.id, locale),
    })
    confirmCancelTransactionModal.close()
    forceRefresh()
  }

  const confirmCancelTransactionModal = useModal(ActionModal, {
    title: t('Transactions.Cancel Transaction'),
    description: t('Transactions.Are you sure you want to cancel your transaction'),
  })

  const drawerModal = useModal(TransactionHistoryDrawerModal, () => ({
    context: 'paymentAgent' as const,
    transactions: data,
    walletId: activeWalletId,
    onCancelTransaction: (txToCancel: TransactionDto) => {
      confirmCancelTransactionModal.open({
        onConfirm: () => onCancelTransaction(txToCancel),
      })
    },
  }))

  const openTransactionDrawer = (tx: TransactionDto) =>
    drawerModal.open({
      initialTransactionId: tx.id,
    })

  useEffect(() => {
    if (drawerModal.isOpen) {
      drawerModal.reRender()
    }
  }, [data, drawerModal.isOpen, drawerModal.reRender])

  useEffect(() => {
    if (location.state?.hasSuccessfulWithdrawAction) {
      scrollIntoView([ScrollToIds.PaymentAgentTransactionHistoryHeader])
    }
  }, [location, scrollIntoView])

  useEffect(
    () => {
      const timer = setTimeout(
        () =>
          searchTerm.searchTerm !== undefined &&
          setPageQuery!({
            ...pageQuery,
            pageIndex: 1,
            ca_search_TextSearch: searchTerm.searchTerm,
          }),
        500
      )
      return () => clearTimeout(timer)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTerm?.searchTerm]
  )

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.PaymentAgentTransactionHistoryHeader,
    isPaginationEntrySelected,
    isFetching,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(ScrollToIds.PaymentAgentTransactionHistoryHeader, isFetching, meta?.pageSize)
  const handleSearchFilters = useCallback(
    (searchFilters?: FilterQueryProps) => {
      if (!searchFilters) return

      setSearch((currentSearch) =>
        Object.keys(searchFilters).length === 0
          ? initialSearchValue
          : {
              ...currentSearch,
              ...searchFilters,
            }
      )

      setPageQuery?.({
        ...pageQuery,
        pageIndex: 1,
      })
    },
    [initialSearchValue, pageQuery, setPageQuery]
  )

  const activeFilters = useMemo(() => {
    return Object.values(search).filter((v) => v !== undefined && v !== '').length
  }, [search])

  const hasSearch = !!searchTerm.searchTerm
  const hasActiveFilters = activeFilters > 0 || hasSearch
  const hasPaginationResults = hasInitialResults || !!data.length

  const content = isMobile ? (
    !data.length && !isFetching ? (
      <TransactionHistoryEmptyState hasActiveFilters={hasActiveFilters} />
    ) : (
      <>
        {data.map((trans, index) => (
          <TransactionHistoryMobileRow
            key={trans.id}
            tx={trans}
            context='paymentAgent'
            walletId={activeWalletId}
            id={getScrollToCardId(index, data.length, 'payment-agent-transaction')}
            onClick={openTransactionDrawer}
          />
        ))}
      </>
    )
  ) : (
    <PaymentAgentTransactionHistoryTable
      data={data}
      pageKey={`${meta?.pageIndex ?? 1}:${meta?.pageSize ?? 'default'}`}
      isLoading={isFetching}
      pageQuery={pageQuery}
      setPageQuery={setPageQuery}
      activeFilters={activeFilters}
      hasSearch={hasSearch}
      onRowClick={openTransactionDrawer}
      walletId={activeWalletId}
    />
  )

  const sortOptions = [
    { name: t('Transactions.Transaction Type'), id: 'TransactionType.name' },
    { name: t('Created Date'), id: 'CreatedDate' },
    { name: t('Transactions.Transaction State'), id: 'TransactionState.name' },
  ]

  return (
    <>
      <PageSection
        id={ScrollToIds.PaymentAgentTransactionHistoryHeader}
        title={t('Payment Agent.Transaction History')}
        searchField={{
          label: t('Search reference'),
          value: searchTerm.searchTerm,
          onChange: (v: string) => showSearchTerm({ ...searchTerm, searchTerm: v }),
          canClear: true,
          isLoading: isFetching,
          renderResult: () => content,
        }}
        pageQuery={pageQuery}
        setPageQuery={setPageQuery}
        sortOptions={sortOptions}
        onFilterClick={() => setIsFilterVisible((s) => !s)}
        documentFile={generateDocumentFile}
      />

      <TableFilter
        isVisible={isFilterVisible}
        searchFilters={search}
        filterOptions={[
          FilterOptions.Date,
          FilterOptions.ClientIdAmount,
          FilterOptions.TransactionType,
          FilterOptions.TransactionStatus,
          FilterOptions.Wallet,
        ]}
        transactionTypes={transactionTypes}
        transactionStates={transactionStates}
        wallets={walletNameOptions}
        setSearchFilters={handleSearchFilters}
      />

      <Loading showLoadingIcon isLoading={isFetching}>
        {content}
      </Loading>

      {meta && hasPaginationResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.PaymentAgentTransactionHistoryHeader}
          maxPageSize={100}
          pageData={meta}
          isLoading={isFetching}
          onPageChanged={(pageIndex, pageSize) => {
            setPageQuery!({ ...pageQuery, pageIndex, pageSize })
          }}
        />
      )}
    </>
  )
}
